/**
 * @file Custom Tooltip Component
 * @description Custom tooltip component for video dropoff calculator charts
 */

import type { CustomTooltipProps } from '~/components/resources/calculators/video-dropoff/types'
import { formatTimeWithPercentage } from '~/components/resources/calculators/video-dropoff/utils/formatters'

/**
 * Custom tooltip component for Recharts charts
 * @param {CustomTooltipProps} props - Tooltip props from Recharts
 * @returns {JSX.Element | null} Rendered tooltip or null if inactive
 */
export const CustomTooltip = ({ 
  active, 
  payload, 
  label, 
  activeView, 
  videoLength 
}: CustomTooltipProps): JSX.Element | null => {
  if (active && payload && payload.length > 0) {
    const data = payload[0]?.payload ?? {}
    const timePosition = data.timestamp ?? 0
    // Provide a default value for videoLength if it's undefined
    const effectiveVideoLength = videoLength ?? 60 
    
    // Format time with percentage
    const displayTime = formatTimeWithPercentage(timePosition, effectiveVideoLength)
    
    // Get the actual value for potential alerts
    const value = payload[0]?.value ?? 0
    
    return (
      <div className="backdrop-blur-sm border shadow-lg p-4 max-w-xs rounded-lg 
                    bg-white/95 dark:bg-gray-800/95 
                    border-gray-200 dark:border-gray-700">
        <div className="border-b pb-2 mb-2 border-gray-200 dark:border-gray-700">
          <p className="font-medium text-gray-900 dark:text-white flex items-center justify-between">
            <span>{data.timeLabel ?? label ?? 'Video Point'}</span>
            <span className="text-xs text-gray-500 dark:text-gray-400 ml-2 px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">
              {displayTime}
            </span>
          </p>
        </div>
        
        <div className="space-y-2">
          {/* Retention / Drop-off % Row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className={`h-3 w-3 rounded-full mr-2 ${activeView === 'retention' ? "bg-indigo-500 dark:bg-indigo-400" : activeView === 'dropoff' ? "bg-purple-500 dark:bg-purple-400" : "bg-orange-500 dark:bg-orange-400"}`}></div>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                {activeView === 'retention' ? "Retention" : activeView === 'dropoff' ? "Drop-off" : "Norm. Rate"}:
              </p>
            </div>
            <p className={`text-sm font-medium ${activeView === 'retention' ? "text-indigo-600 dark:text-indigo-400" : activeView === 'dropoff' ? "text-purple-600 dark:text-purple-400" : "text-orange-600 dark:text-orange-400"}`}>
                {/* Show normalizedRate for the new view */}
                {activeView === 'normalizedDropoff' 
                  ? `${data.normalizedRate?.toFixed(1) ?? '0'}% / sec`
                  : `${payload[0]?.value?.toFixed(1) ?? '0'}%`}
            </p>
          </div>
          
          {/* Absolute Viewers Row (Only for Retention/Dropoff) */}
          {activeView !== 'normalizedDropoff' && (
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="h-3 w-3 rounded-full bg-green-500 dark:bg-green-400 mr-2"></div>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  {activeView === 'retention' ? "Viewers" : "Viewers Lost"}:
                </p>
              </div>
              <p className="text-sm font-medium text-green-600 dark:text-green-400">
                {data.absoluteValue?.toLocaleString() ?? '0'}
              </p>
            </div>
          )}

          {/* Display Raw Drop-off Rate and Interval for Normalized View */}          
          {activeView === 'normalizedDropoff' && (
            <div className="flex items-center justify-between border-t pt-2 mt-2 border-dashed border-gray-200 dark:border-gray-700">
              <div className="flex items-center">
                <div className="h-3 w-3 rounded-full bg-purple-500 dark:bg-purple-400 mr-2"></div>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Seg. Totals:
                </p>
              </div>
              <p className="text-sm font-medium text-purple-600 dark:text-purple-400">
                {data.dropoffRate?.toFixed(1) ?? '0'}% ({data.interval?.toFixed(1) ?? '0'}s)
              </p>
            </div>
          )}
        </div>
        
        {/* High Dropoff Alert (Show for Dropoff and Normalized views) */}
        {activeView !== 'retention' && data.isHighDropoff && (
          <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
            <div className="flex flex-col items-start text-xs font-medium text-red-600 dark:text-red-400">
              <span className="flex items-center">
                <span className="inline-block h-2 w-2 rounded-full bg-red-500 mr-1.5 animate-pulse"></span>
                Significant Drop-off Point!
              </span>
              <span>
                <p className="text-xs mt-1 text-red-600/80 dark:text-red-400/80">
                  This segment has significantly higher viewer loss than expected based on its position and duration.
                  {typeof data.interval === 'number' && data.interval > 0 && data.dropoffRate && (
                    <span> ({(data.dropoffRate / data.interval).toFixed(1)}% per second)</span>
                  )}
                </p>
              </span>
            </div>
          </div>
        )}
        
        {activeView === 'retention' && value < 50 && (data.percentComplete ?? 0) > 25 && (
          <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center text-xs font-medium text-amber-600 dark:text-amber-400">
              <span className="inline-block h-2 w-2 rounded-full bg-amber-500 mr-1.5"></span>
              Low retention for this point in the video.
            </div>
          </div>
        )}
      </div>
    )
  }
  return null
}
